import { api } from '../console/api.ts';
import { field, pill } from '../console/components.ts';
import { InputError } from '../console/errors.ts';
import { date, esc, icon, person, when } from '../console/format.ts';
import { type Actions, current, render } from '../console/router.ts';
import { $, act } from '../console/ui.ts';
import {
  checkboxControl,
  dateControl,
  selectControl,
  textareaControl,
  textControl,
} from '../controls.ts';

/**
 * The follow-up of what the agency works on (#317, ADR-0043): its tasks (who must do what by when)
 * and its notes. Shared by «Mis tareas», the customer's record, a policy's page and a claim: a task
 * row with its actions (done with the outcome, cancelled with the reason, handed to another
 * person), the open tasks of a subject with «Nueva tarea», and the notes of a policy, a quote or a
 * claim (pinned first; their author corrects them within 24 hours).
 */

export type TaskSubject = {
  kind: string;
  id: string;
  item: number | null;
  label: string | null;
};
export type TaskRow = {
  id: string;
  title: string;
  kind: string;
  priority: string;
  dueAt: string;
  dueDate: string;
  status: string;
  bucket: 'OVERDUE' | 'TODAY' | 'UPCOMING' | 'CLOSED';
  outcome: string | null;
  note: string | null;
  subject: TaskSubject | null;
  assignee: { memberId: string; name: string | null };
  automatic: boolean;
  can: { update: boolean; reassign: boolean };
};
export type NoteRow = {
  id: string;
  body: string;
  pinned: boolean;
  at: string;
  by: string;
  byName?: string | null;
  editedAt: string | null;
  editable: boolean;
};
export type Person = { memberId: string; name: string; role: string | null };

export const TASK_KIND: Record<string, string> = {
  CALL: 'Llamada',
  WHATSAPP: 'WhatsApp',
  VISIT: 'Visita',
  MEETING: 'Reunión',
  PAPERWORK: 'Trámite',
  OTHER: 'Otro',
};
export const TASK_PRIORITY: Record<string, [label: string, tone: string]> = {
  HIGH: ['Alta', 'bad'],
  NORMAL: ['Normal', 'info'],
  LOW: ['Baja', 'DRAFT'],
};
export const TASK_STATUS: Record<string, [label: string, tone: string]> = {
  PENDING: ['Pendiente', 'info'],
  DONE: ['Hecha', 'ok'],
  CANCELLED: ['Anulada', 'CANCELLED'],
};
const SUBJECT_ICON: Record<string, string> = {
  PARTY: 'client',
  POLICY: 'policy',
  RECEIPT: 'receipt',
  QUOTE: 'quote',
  CLAIM: 'alert',
  RENEWAL: 'trend',
};

/**
 * «hoy 10:00» · «24 sep 15:30»: the due time as the agency reads it, in Guatemala. Whether it is
 * today or overdue is the API's (`bucket`, on the business clock), never this browser's clock.
 */
const HOUR = new Intl.DateTimeFormat('es-GT', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'America/Guatemala',
});
export const dueText = (t: Pick<TaskRow, 'dueAt' | 'bucket'>) =>
  t.bucket === 'TODAY' ? `hoy ${HOUR.format(new Date(t.dueAt))}` : when(t.dueAt);

/** Who a task is for, by name (their member id when the team has no name for it). */
export const assigneeName = (t: Pick<TaskRow, 'assignee'>) =>
  t.assignee.name ?? person(`human:${t.assignee.memberId}`);

/** An id safe for the DOM from a subject («POLICY:gyt:AUTO-1:2026-09-23» → «POLICY_gyt_AUTO-1_…»). */
export const domKey = (s: string) => s.replace(/[^A-Za-z0-9_-]/g, '_');

/** The task being closed or handed over (its inline form stays open across a repaint). */
let closing: { id: string; mode: 'done' | 'cancel' | 'reassign' } | null = null;
/** The note being corrected (its subject's notes show its form). */
let editingNote: string | null = null;
/** Whom management can give a task to (loaded once, when a form needs it). */
let people: Person[] | null = null;

/** A new session starts with nothing open. */
export function resetFollowUp() {
  closing = null;
  editingNote = null;
  people = null;
}

/** The agency's people for a «Responsable» select (management; everyone else, only themselves). */
export async function loadPeople(): Promise<Person[]> {
  if (!people) people = (await api<{ items: Person[] }>('GET', '/v1/tasks/people')).items;
  return people;
}

/** What a task is about, as a chip («Póliza P-1 · Luis Ramírez»). */
export const subjectChip = (s: TaskSubject | null) =>
  s?.label
    ? `<span class="chip task-subject">${icon(SUBJECT_ICON[s.kind] ?? 'file')}${esc(s.label)}</span>`
    : '';

/** The inline form that closes or hands over a task. */
function closingForm(t: TaskRow, choices: Person[]) {
  if (closing?.id !== t.id) return '';
  const id = esc(t.id);
  const control =
    closing.mode === 'reassign'
      ? field(
          'Nuevo responsable',
          selectControl(
            { id: 'tc-person' },
            choices
              .filter((p) => p.memberId !== t.assignee.memberId)
              .map((p) => ({ value: p.memberId, label: p.name })),
          ),
        )
      : field(
          closing.mode === 'done' ? 'Resultado' : 'Motivo de la anulación',
          textControl({
            id: 'tc-text',
            maxlength: 1000,
            placeholder:
              closing.mode === 'done' ? 'Confirmó la placa P-123ABC' : 'El cliente ya pagó en caja',
          }),
          { required: true },
        );
  const verb =
    closing.mode === 'done'
      ? 'Marcar hecha'
      : closing.mode === 'cancel'
        ? 'Anular tarea'
        : 'Reasignar';
  return `<div class="task-closing toolbar" data-component="task-closing"><div class="grow">${control}</div>
    <button type="button" class="btn sm" data-task-close-cancel>Cancelar</button>
    <button type="button" class="btn sm ${closing.mode === 'cancel' ? 'danger' : 'primary'}" data-task-confirm="${id}" data-mode="${closing.mode}">${verb}</button></div>`;
}

/**
 * One task as a list item: due time, title, what it is about, its kind and priority, who does it
 * (`withAssignee`), its status and its actions.
 */
export function taskItemHtml(
  t: TaskRow,
  opts: { withAssignee?: boolean; choices?: Person[] } = {},
) {
  const [priority, tone] = TASK_PRIORITY[t.priority] ?? [t.priority, 'info'];
  const [status, statusTone] = TASK_STATUS[t.status] ?? [t.status, 'info'];
  const due =
    t.bucket === 'OVERDUE'
      ? `<span class="task-due late">${icon('clock')}Venció ${esc(dueText(t))}</span>`
      : `<span class="task-due">${icon('clock')}${esc(dueText(t))}</span>`;
  const actions =
    t.status === 'PENDING'
      ? [
          t.can.update
            ? `<button type="button" class="btn sm" data-task-close="${esc(t.id)}" data-mode="done" aria-label="Marcar hecha «${esc(t.title)}»">${icon('ok')}Hecha</button>`
            : '',
          t.can.reassign
            ? `<button type="button" class="btn sm" data-task-close="${esc(t.id)}" data-mode="reassign" aria-label="Reasignar «${esc(t.title)}»">Reasignar</button>`
            : '',
          t.can.update
            ? `<button type="button" class="btn sm" data-task-close="${esc(t.id)}" data-mode="cancel" aria-label="Anular «${esc(t.title)}»">Anular</button>`
            : '',
        ].join('')
      : '';
  return `<li class="task-item ${t.bucket === 'OVERDUE' ? 'overdue' : ''} ${t.status !== 'PENDING' ? 'closed' : ''}" data-task="${esc(t.id)}">
    <div class="task-main">
      <div class="task-line">${due}<strong class="task-title">${esc(t.title)}</strong></div>
      <div class="task-meta">${pill('info', TASK_KIND[t.kind] ?? t.kind)}${t.priority !== 'NORMAL' ? pill(tone, `Prioridad ${priority.toLowerCase()}`) : ''}${t.status !== 'PENDING' ? pill(statusTone, status) : ''}${t.automatic ? '<span class="chip">Automática</span>' : ''}${subjectChip(t.subject)}${opts.withAssignee ? `<span class="muted small">${icon('users')}${esc(assigneeName(t))}</span>` : ''}</div>
      ${t.note ? `<p class="muted small task-note">${esc(t.note)}</p>` : ''}
      ${t.outcome ? `<p class="small task-outcome"><strong>${t.status === 'DONE' ? 'Resultado' : 'Motivo'}:</strong> ${esc(t.outcome)}</p>` : ''}
    </div>
    ${actions ? `<div class="task-actions">${actions}</div>` : ''}
    ${closingForm(t, opts.choices ?? [])}
  </li>`;
}

/** The fields of a new task (title, kind, priority, due date and hour; the person for management). */
export function newTaskFields(key: string, today: string, choices: Person[] | null) {
  return `<div class="task-form-grid">
    <div class="grow task-form-title">${field('Qué hay que hacer', textControl({ id: `tn-title-${key}`, maxlength: 200, placeholder: 'Llamar para confirmar placa' }), { required: true })}</div>
    ${field(
      'Tipo',
      selectControl(
        { id: `tn-kind-${key}` },
        Object.entries(TASK_KIND).map(([value, label]) => ({ value, label })),
      ),
    )}
    ${field(
      'Prioridad',
      selectControl(
        { id: `tn-priority-${key}` },
        Object.entries(TASK_PRIORITY).map(([value, [label]]) => ({
          value,
          label,
          selected: value === 'NORMAL',
        })),
      ),
    )}
    ${field('Vence', dateControl({ id: `tn-date-${key}`, value: today, min: today }), { required: true })}
    ${field('Hora', textControl({ id: `tn-time-${key}`, type: 'time', value: '09:00' }))}
    ${
      choices && choices.length > 1
        ? field(
            'Responsable',
            selectControl(
              { id: `tn-assignee-${key}` },
              choices.map((p) => ({ value: p.memberId, label: p.name })),
            ),
          )
        : ''
    }
    <div class="grow task-form-note">${field('Nota (opcional)', textControl({ id: `tn-note-${key}`, maxlength: 2000, placeholder: 'Pedir foto de la tarjeta de circulación' }))}</div>
  </div>`;
}

/** Reads the new task's fields; throws the first one to fix. */
export function readNewTask(key: string) {
  const v = (id: string) => ($(`#${id}`) as HTMLInputElement | null)?.value?.trim() ?? '';
  const title = v(`tn-title-${key}`);
  if (!title) throw new InputError('Escribe qué hay que hacer.', `tn-title-${key}`);
  const dueDate = v(`tn-date-${key}`);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dueDate))
    throw new InputError('Elige el día en que vence.', `tn-date-${key}`);
  const dueTime = v(`tn-time-${key}`) || '09:00';
  const assignee = v(`tn-assignee-${key}`);
  const note = v(`tn-note-${key}`);
  return {
    title,
    kind: v(`tn-kind-${key}`) || 'CALL',
    priority: v(`tn-priority-${key}`) || 'NORMAL',
    dueDate,
    dueTime: dueTime.slice(0, 5),
    ...(assignee ? { assignee } : {}),
    ...(note ? { note } : {}),
  };
}

/**
 * The open tasks of a subject (a customer, a policy, a claim) and «Nueva tarea» about it (due
 * today by default).
 */
export function subjectTasksHtml(
  tasks: TaskRow[],
  subject: { kind: string; id: string },
  opts: { canCreate?: boolean; choices?: Person[] | null } = {},
) {
  const key = domKey(`${subject.kind}-${subject.id}`);
  const list = tasks.length
    ? `<ul class="task-list" data-component="task-list">${tasks.map((t) => taskItemHtml(t, { withAssignee: true, choices: opts.choices ?? [] })).join('')}</ul>`
    : '<p class="muted party-none">No hay tareas abiertas.</p>';
  const form =
    opts.canCreate === false
      ? ''
      : `<details class="task-new" data-component="task-new"><summary class="btn sm">${icon('calendar')}Nueva tarea</summary>
      <div class="task-new-body">${newTaskFields(key, todayInGuatemala(), opts.choices ?? null)}
      <div class="order-buttons"><button type="button" class="btn primary" data-task-new="${esc(key)}" data-subject-kind="${esc(subject.kind)}" data-subject-id="${esc(subject.id)}">Crear tarea</button></div></div></details>`;
  return `<section class="follow-up-tasks" aria-label="Tareas abiertas"><h3>Tareas abiertas <span class="tab-count">${tasks.length}</span></h3>${list}${form}</section>`;
}

/** The notes of a policy, a quote or a claim: pinned first; their author corrects them in 24 hours. */
export function subjectNotesHtml(
  notes: NoteRow[],
  subject: { kind: string; id: string },
  canNote: boolean,
  opts: { form?: boolean } = {},
) {
  const key = domKey(`${subject.kind}-${subject.id}`);
  const ref = `data-subject-kind="${esc(subject.kind)}" data-subject-id="${esc(subject.id)}"`;
  const list = notes.length
    ? `<ul class="party-list party-notes" data-component="notes">${notes
        .map((n) =>
          n.id === editingNote && n.editable
            ? `<li><div class="field grow">${textareaControl({ label: 'Corregir la nota', id: `sn-fix-${key}`, maxlength: 2000, rows: 2, value: n.body })}</div>
              <div class="order-buttons"><button type="button" class="btn sm" data-snote-cancel>Cancelar</button><button type="button" class="btn sm primary" data-snote-save="${esc(n.id)}" ${ref}>Guardar corrección</button></div></li>`
            : `<li class="${n.pinned ? 'pinned' : ''}"><div class="grow">${n.pinned ? `${pill('info', 'Fijada')} ` : ''}${n.body ? esc(n.body) : '<span class="muted">Nota borrada a pedido del cliente.</span>'}
          <div class="muted small">${esc(when(n.at))} · ${esc(n.byName ?? person(n.by))}${n.editedAt ? ' · corregida' : ''}</div></div>
          ${
            canNote && n.body
              ? `<div class="order-buttons">${n.editable ? `<button type="button" class="btn sm" data-snote-edit="${esc(n.id)}">Corregir</button>` : ''}<button type="button" class="btn sm" data-snote-pin="${esc(n.id)}" data-pinned="${n.pinned ? 'false' : 'true'}" ${ref}>${n.pinned ? 'Desfijar' : 'Fijar'}</button></div>`
              : ''
          }</li>`,
        )
        .join('')}</ul>`
    : '<p class="muted party-none">Todavía no hay notas.</p>';
  const form =
    canNote && opts.form !== false
      ? `<div class="toolbar note-form">
      <div class="field grow">${textareaControl({ label: 'Nota', id: `sn-body-${key}`, maxlength: 2000, rows: 2, placeholder: 'Pidió cambiar la forma de pago' })}</div>
      <div class="field">${checkboxControl({ id: `sn-pin-${key}`, class: 'check' }, 'Fijar arriba')}</div>
      <div class="order-buttons"><button type="button" class="btn" data-snote-add="${esc(key)}" ${ref}>Agregar nota</button></div></div>
      <p class="muted small party-consent-note">Quien la escribe puede corregirla durante 24 horas; después queda como está y se agrega una nueva.</p>`
      : '';
  return `<section class="follow-up-notes" aria-label="Notas"><h3>Notas</h3>${list}${form}</section>`;
}

/** Tasks and notes side by side (a policy's tab, a claim). */
export function followUpHtml(
  d: { tasks: TaskRow[]; notes: NoteRow[]; canNote: boolean; choices?: Person[] | null },
  subject: { kind: string; id: string },
) {
  return `<div class="follow-up" data-component="follow-up">${subjectTasksHtml(d.tasks, subject, { choices: d.choices ?? null })}${subjectNotesHtml(d.notes, subject, d.canNote)}</div>`;
}

/** Today in Guatemala as this browser's clock says it: only the new task form's default date. */
export const todayInGuatemala = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Guatemala' }).format(new Date());

const repaint = () => render(current, { quiet: true });
const subjectOf = (d: DOMStringMap) => ({ kind: d.subjectKind ?? '', id: d.subjectId ?? '' });

export const actions: Actions = {
  click: {
    taskClose: (_, d) => {
      const id = d.taskClose;
      const mode = d.mode as 'done' | 'cancel' | 'reassign' | undefined;
      if (!id || !mode) return;
      closing = { id, mode };
      const ready = mode === 'reassign' ? loadPeople().then(() => undefined) : Promise.resolve();
      void ready.then(repaint).then(() => ($('#tc-text') ?? $('#tc-person'))?.focus());
    },
    taskCloseCancel: () => {
      closing = null;
      void repaint();
    },
    taskConfirm: (t, d) => {
      const id = d.taskConfirm;
      const mode = d.mode;
      if (!id || !mode) return;
      const text = ($('#tc-text') as HTMLInputElement | null)?.value?.trim() ?? '';
      const to = ($('#tc-person') as HTMLInputElement | null)?.value ?? '';
      void act(
        t,
        mode === 'done' ? 'Cerrando la tarea…' : mode === 'cancel' ? 'Anulando…' : 'Reasignando…',
        async () => {
          const path = `/v1/tasks/${encodeURIComponent(id)}`;
          if (mode === 'reassign') {
            if (!to) throw new InputError('Elige a quién se la das.', 'tc-person');
            await api('POST', `${path}/reassign`, { assignee: to });
          } else {
            if (!text)
              throw new InputError(
                mode === 'done' ? 'Escribe el resultado.' : 'Escribe por qué se anula.',
                'tc-text',
              );
            await api('POST', `${path}/${mode === 'done' ? 'done' : 'cancel'}`, {
              [mode === 'done' ? 'outcome' : 'reason']: text,
            });
          }
          closing = null;
          await repaint();
          return mode === 'done'
            ? 'Tarea hecha.'
            : mode === 'cancel'
              ? 'Tarea anulada.'
              : 'Tarea reasignada.';
        },
      );
    },
    taskNew: (t, d) => {
      const key = d.taskNew;
      if (!key) return;
      const subject = d.subjectKind ? subjectOf(d) : null;
      void act(t, 'Creando la tarea…', async () => {
        const body = readNewTask(key);
        await api('POST', '/v1/tasks', { ...body, ...(subject ? { subject } : {}) });
        await repaint();
        return `Tarea creada para el ${date(body.dueDate)}.`;
      });
    },
    snoteAdd: (t, d) => {
      const key = d.snoteAdd;
      if (!key) return;
      const body = ($(`#sn-body-${key}`) as HTMLInputElement | null)?.value?.trim() ?? '';
      const pinned = ($(`#sn-pin-${key}`) as HTMLInputElement | null)?.checked === true;
      void act(t, 'Guardando la nota…', async () => {
        if (!body) throw new InputError('Escribe la nota.', `sn-body-${key}`);
        await api('POST', '/v1/notes', { subject: subjectOf(d), body, pinned });
        await repaint();
        return pinned ? 'Nota agregada y fijada.' : 'Nota agregada.';
      });
    },
    snotePin: (t, d) => {
      const id = d.snotePin;
      if (!id) return;
      const pinned = d.pinned === 'true';
      void act(t, 'Guardando…', async () => {
        await api('POST', `/v1/notes/${encodeURIComponent(id)}/pin`, {
          subject: subjectOf(d),
          pinned,
        });
        await repaint();
        return pinned ? 'Nota fijada.' : 'Nota desfijada.';
      });
    },
    snoteEdit: (_, d) => {
      editingNote = d.snoteEdit ?? null;
      void repaint().then(() => document.querySelector<HTMLElement>('[id^="sn-fix-"]')?.focus());
    },
    snoteCancel: () => {
      editingNote = null;
      void repaint();
    },
    snoteSave: (t, d) => {
      const id = d.snoteSave;
      if (!id) return;
      const subject = subjectOf(d);
      const key = domKey(`${subject.kind}-${subject.id}`);
      const body = ($(`#sn-fix-${key}`) as HTMLInputElement | null)?.value?.trim() ?? '';
      void act(t, 'Corrigiendo la nota…', async () => {
        if (!body) throw new InputError('Escribe la nota.', `sn-fix-${key}`);
        await api('PUT', `/v1/notes/${encodeURIComponent(id)}`, { subject, body });
        editingNote = null;
        await repaint();
        return 'Nota corregida.';
      });
    },
  },
};
